import { FormControl, FormErrorMessage } from "@chakra-ui/react";
import { FormFieldType } from "@models/form";
import { DEFAULT_LANGUAGE, Language, translate as t } from "@models/language";
import { REGEX_EMAIL } from "@models/regex";
import dynamic from "next/dynamic";
import { useEffect } from "react";
import {
  FieldErrors,
  FieldValues,
  RegisterOptions,
  UseFormRegister,
  UseFormSetValue,
  UseFormUnregister,
  UseFormWatch,
} from "react-hook-form";
import DynamicFormFieldHeader from "./DynamicFormFieldHeader";
import { Field } from "./types";

// https://github.com/zenoamaro/react-quill/issues/718
const ReactQuill = dynamic(() => import("react-quill"), { ssr: false });

const modules = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      // ["blockquote", "code-block"],
      // [{ script: "sub" }, { script: "super" }], // superscript/subscript
      [{ list: "ordered" }, { list: "bullet" }],
      // [{ indent: "-1" }, { indent: "+1" }],
      ["link"],
      // [{ color: [] }, { background: [] }],
      // [{ align: [] }],
      ["clean"],
      // ["image"],
    ],
  },
  // keyboard: {
  //   bindings: {
  //     indent: false,
  //     outdent: false,
  //   },
  // },
  // clipboard: {
  //   matchers: [
  //     [
  //       1,
  //       (node, delta) => {
  //         let ops: any[] = [];
  //         console.log(node, delta);
  //         delta.ops.forEach((op) => {
  //           if (op.insert && typeof op.insert === "string") {
  //             ops.push({
  //               insert: op.insert,
  //             });
  //           }
  //         });
  //         delta.ops = ops;
  //         console.log(delta);
  //         return delta;
  //       },
  //     ],
  //   ],
  // },
};

const DynamicFormRichText = ({
  field,
  errors,
  register,
  unregister,
  setValue,
  watch,
  language: language = DEFAULT_LANGUAGE,
  isDirty: isDirty = true,
  className: className = "",
}: {
  field: Field;
  errors: FieldErrors<FieldValues>;
  register: UseFormRegister<FieldValues>;
  unregister: UseFormUnregister<FieldValues>;
  language?: Language;
  isDirty?: boolean;
  className?: string;
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
}) => {
  // TODO: default translation
  const _t = (key?: string, language = DEFAULT_LANGUAGE) => key || "";

  // https://tailwindcss.com/docs/content-configuration#dynamic-class-names
  let colSpan = "sm:col-span-1";
  switch (field.span) {
    case 1:
      colSpan = "sm:col-span-1";
      break;
    case 2:
      colSpan = "sm:col-span-2";
      break;
    case 3:
      colSpan = "sm:col-span-3";
      break;
    case 4:
      colSpan = "sm:col-span-4";
      break;
  }

  const fieldValue = watch(field.key);

  const toRegisterOptions = (
    field: Field
  ): RegisterOptions<FieldValues, string> => {
    return {
      ...(field.minLength
        ? {
            minLength: {
              value: field.minLength,
              message:
                t(field.errorMessages?.minLength, language) ||
                _t(`Minimum length is ${field.max}`, language),
            },
          }
        : {}),
      ...(field.maxLength
        ? {
            maxLength: {
              value: field.maxLength,
              message:
                t(field.errorMessages?.maxLength, language) ||
                _t(`Maximum length is ${field.max}`, language),
            },
          }
        : {}),
      // in the future, if field.pattern is defined, reeuired doesn't work
      ...(field.required
        ? {
            pattern: {
              value: new RegExp("^(?!<p><br></p>$).*$", "gm"),
              message:
                t(field.errorMessages?.required, language) ||
                _t("This field is required", language),
            },
            required:
              t(field.errorMessages?.required, language) ||
              _t("This field is required", language),
          }
        : {}),
      ...(field.pattern
        ? {
            pattern: {
              value: new RegExp(field.pattern, "gm"),
              message:
                t(field.errorMessages?.pattern, language) ||
                _t("The input is not valid", language),
            },
          }
        : field.type === FormFieldType.EMAIL
        ? {
            pattern: {
              value: REGEX_EMAIL,
              message:
                t(field.errorMessages?.pattern, language) ||
                _t("Invalid email address", language),
            },
          }
        : {}),
      ...(field.validate
        ? {
            validate: field.validate,
          }
        : {}),
    };
  };

  useEffect(() => {
    register(field.key, {
      ...toRegisterOptions(field),
    });

    return () => {
      unregister(field.key, { keepDefaultValue: true });
    };
  }, [register]);

  const handleQuillChange = (value) => {
    setValue(field.key, value, { shouldDirty: true });
  };

  return (
    <>
      <FormControl
        isInvalid={isDirty && !!errors[field.key]}
        className={`${field.span ? colSpan : ""} col-span-1 ${className}`}
        isRequired={field.required || field.confirm}
        isDisabled={field.disabled}
        isReadOnly={field.readonly}
      >
        <div
          onMouseEnter={() => {
            field?.updateCurrentEventPreviewPart &&
              field?.updateCurrentEventPreviewPart(field.key);
          }}
          onMouseLeave={() => {
            if (field?.updateCurrentEventPreviewPart) {
              field?.updateCurrentEventPreviewPart("DUMMY");
            }
          }}
        >
          <DynamicFormFieldHeader field={field} language={language} />
        </div>
        <div>
          <ReactQuill
            onChange={handleQuillChange}
            placeholder={t(field.placeholder, language)}
            value={fieldValue}
            modules={modules}
          />
        </div>

        <FormErrorMessage>
          {errors[field.key] && String(errors[field.key]?.message || "")}
        </FormErrorMessage>
      </FormControl>
    </>
  );
};

export default DynamicFormRichText;
